/**
 * ViewContext.js
 * M-03: Manages active view context for multicentro/multi-role switching.
 * Stores the user's active role×scope context and whether they're in employee view.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const ViewContext = createContext();

export const useView = () => {
  const context = useContext(ViewContext);
  if (!context) throw new Error('useView must be used within a ViewProvider');
  return context;
};

export const ViewProvider = ({ children }) => {
  const { user } = useAuth();
  const [contexts, setContexts] = useState([]);
  const [activeContext, setActiveContextState] = useState(null);
  const [isEmployeeView, setIsEmployeeView] = useState(false);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loadingContexts, setLoadingContexts] = useState(false);

  // Load user's available contexts from backend
  const loadContexts = useCallback(async () => {
    if (!user) return;
    setLoadingContexts(true);
    try {
      const res = await axios.get('/permissions/my-permissions');
      if (res.data?.success) {
        const rolesDetail = res.data.data.roles_detail || [];
        // Build contexts: each role×scope is one context
        const ctxs = rolesDetail
          .filter(r => r.role !== 'employee') // employee view handled separately
          .map(r => ({
            role: r.role,
            roleName: r.name_es || r.role,
            roleNameEn: r.name_en || r.role,
            scopeType: r.scope_type,
            scopeId: r.scope_id,
            scopeName: r.scope_name,
            source: r.source,
            label_es: buildLabel(r, 'es'),
            label_en: buildLabel(r, 'en'),
          }));
        setContexts(ctxs);

        // Set default active context (first admin-like or first context)
        if (!activeContext && ctxs.length > 0) {
          setActiveContextState(ctxs[0]);
        }
      }
    } catch (e) {
      logger.error('Error loading view contexts:', e);
    } finally {
      setLoadingContexts(false);
    }
  }, [user]);

  // Load pending employee tasks
  const loadPendingTasks = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get('/auth/me');
      if (!res.data?.success) return;
      const userId = res.data.data.id;
      // Check for pending assessments
      const assessRes = await axios.get(`/assessments/user/${userId}`);
      const pending = [];
      if (assessRes.data?.success) {
        const pendingAssessments = (assessRes.data.data || []).filter(
          a => a.status === 'pendiente' || a.status === 'en_progreso'
        );
        pendingAssessments.forEach(a => {
          pending.push({
            type: 'assessment',
            id: a.id,
            title: a.tipo === 'nom035' ? 'Evaluación NOM-035' : `Evaluación ${a.tipo || ''}`,
            status: a.status,
          });
        });
      }
      setPendingTasks(pending);
    } catch {
      // Silent - don't block
    }
  }, [user]);

  useEffect(() => { loadContexts(); }, [loadContexts]);
  useEffect(() => { loadPendingTasks(); }, [loadPendingTasks]);

  const setActiveContext = (ctx) => {
    setActiveContextState(ctx);
    setIsEmployeeView(false);
  };

  const switchToEmployeeView = () => {
    setIsEmployeeView(true);
  };

  const switchToAdminView = () => {
    setIsEmployeeView(false);
    if (!activeContext && contexts.length > 0) {
      setActiveContextState(contexts[0]);
    }
  };

  // Determine if user has admin contexts (not just employee)
  const hasAdminContexts = contexts.length > 0;
  // Determine if user is also an employee
  const isAlsoEmployee = user?.rol === 'employee' || user?.rol === 'company_admin' || true; // all users can have employee view

  return (
    <ViewContext.Provider value={{
      contexts,
      activeContext,
      setActiveContext,
      isEmployeeView,
      switchToEmployeeView,
      switchToAdminView,
      hasAdminContexts,
      isAlsoEmployee,
      pendingTasks,
      loadingContexts,
      refreshContexts: loadContexts,
      refreshPendingTasks: loadPendingTasks,
    }}>
      {children}
    </ViewContext.Provider>
  );
};

function buildLabel(r, lang) {
  const name = lang === 'es' ? (r.name_es || r.role) : (r.name_en || r.role);
  if (r.scope_type === 'cliente') return name;
  const scopeLabel = r.scope_name || r.scope_id || '';
  if (r.scope_type === 'razon_social') {
    return `${name} — RS: ${scopeLabel}`;
  }
  if (r.scope_type === 'centro') {
    return `${name} — ${scopeLabel}`;
  }
  return name;
}
